import { List, Badge, Empty, Tabs, Button, Space, Spin, Select, DatePicker, Tooltip, Tag } from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  UserOutlined,
  ContainerOutlined,
  WarningOutlined,
  BookOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PlusCircleOutlined,
  AppstoreAddOutlined,
  ReloadOutlined,
  CloseOutlined,
  FilterOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { useNotifications } from '../../context/NotificationContext';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

const { RangePicker } = DatePicker;

const NotificationPanel = ({ onClose }) => {
  const {
    notifications = [],
    markAsRead,
    markAllAsRead,
    unreadCount = 0,
    notificationMetadata,
    isLoadingNotifications,
    refreshNotifications,
    loadMoreNotifications,
    updateFilters,
    notificationFilters
  } = useNotifications() || {};
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);

  // Notification Type ID to Name mapping
  const typeMap = {
    3: 'Profile data validation',
    6: 'Document received',
    7: 'Document validated',
    8: 'Document cancelled',
    10: 'User profile changed',
    11: 'Taxpayer profile changed',
    15: 'Document rejection initiated',
    26: 'ERP data validation',
    33: 'Documents processing summary',
    34: 'Document Template Published',
    35: 'Document Template Deletion'
  };

  // Notification Status ID to Name mapping
  const statusMap = {
    1: 'New',
    2: 'Pending',
    3: 'Batched',
    4: 'Delivered',
    5: 'Error'
  };

  // Language options
  const languageOptions = [
    { value: 'ms', label: 'Bahasa Malaysia' },
    { value: 'en', label: 'English' }
  ];

  // Type filter options
  const typeOptions = Object.entries(typeMap).map(([id, name]) => ({
    value: id,
    label: name
  }));

  // Status filter options
  const statusOptions = Object.entries(statusMap).map(([id, name]) => ({
    value: id,
    label: name
  }));

  const getNotificationIcon = (typeName, statusName, color) => {
    const iconStyle = { color, fontSize: '17px' };

    switch (statusName) {
      case 'New':
        return <PlusCircleOutlined style={iconStyle} />;
      case 'Pending':
        return <ClockCircleOutlined style={iconStyle} />;
      case 'Batched':
        return <AppstoreAddOutlined style={iconStyle} />;
      case 'Delivered':
        return <CheckCircleOutlined style={iconStyle} />;
      case 'Error':
        return <ExclamationCircleOutlined style={iconStyle} />;
      default:
        switch (typeName) {
          case 'Document received':
          case 'Document validated':
          case 'Document cancelled':
          case 'Document rejection initiated':
            return <ContainerOutlined style={iconStyle} />;
          case 'Profile data validation':
          case 'ERP data validation':
            return <WarningOutlined style={iconStyle} />;
          case 'User profile changed':
          case 'Taxpayer profile changed':
            return <UserOutlined style={iconStyle} />;
          case 'Document Template Published':
          case 'Document Template Deletion':
            return <BookOutlined style={iconStyle} />;
          case 'Documents processing summary':
            return <InfoCircleOutlined style={iconStyle} />;
          default:
            return <BellOutlined style={iconStyle} />;
        }
    }
  };

  const getNotificationColor = (typeName, statusName) => {
    switch (statusName) {
      case 'New': return '#1890ff';
      case 'Pending': return '#faad14';
      case 'Batched': return '#722ed1';
      case 'Delivered': return '#52c41a';
      case 'Error': return '#ff4d4f';
      default:
        switch (typeName) {
          case 'Document received':
          case 'Document validated': return '#52c41a';
          case 'Document cancelled':
          case 'Document rejection initiated': return '#ff4d4f';
          case 'Profile data validation':
          case 'ERP data validation': return '#faad14';
          case 'User profile changed':
          case 'Taxpayer profile changed': return '#1890ff';
          case 'Document Template Published':
          case 'Document Template Deletion': return '#722ed1';
          case 'Documents processing summary': return '#13c2c2';
          default: return '#8c8c8c';
        }
    }
  };

  const getStatusTagColor = (statusName) => {
    switch (statusName) {
      case 'Delivered': return 'success';
      case 'Error': return 'error';
      case 'Pending': return 'warning';
      case 'New': return 'processing';
      case 'Batched': return 'purple';
      default: return 'default';
    }
  };

  const handleTypeFilter = (value) => updateFilters({ type: value });
  const handleStatusFilter = (value) => updateFilters({ status: value });
  const handleLanguageFilter = (value) => updateFilters({ language: value });

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      updateFilters({ dateFrom: dates[0], dateTo: dates[1] });
    } else {
      updateFilters({ dateFrom: null, dateTo: null });
    }
  };

  const clearFilters = () => {
    updateFilters({ type: null, status: null, language: null, dateFrom: null, dateTo: null });
  };

  const hasActiveFilters = notificationFilters.type || notificationFilters.status || 
    notificationFilters.language || notificationFilters.dateFrom;

  const renderNotificationItem = (notification) => {
    const typeId = notification.originalData?.typeId || notification.originalData?.notificationTypeId;
    const statusId = notification.originalData?.status;
    const typeName = typeMap[typeId] || 'Unknown Type';
    const statusName = statusMap[statusId] || statusId || 'Unknown Status';
    const iconColor = getNotificationColor(typeName, statusName);

    return (
      <List.Item
        className="notification-item"
        onClick={() => {
          if (!notification.read) markAsRead(notification.id);
          navigate(`/notifications/${notification.id}`);
          onClose?.();
        }}
        style={{
          padding: '12px 16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: notification.read ? '#fafafa' : '#ffffff'
        }}
      >
        <List.Item.Meta
          avatar={
            <div style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: `${iconColor}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              {getNotificationIcon(typeName, statusName, iconColor)}
            </div>
          }
          title={
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '6px'
            }}>
              <span style={{ 
                fontSize: '13px',
                fontWeight: notification.read ? 400 : 600,
                color: notification.read ? '#595959' : '#262626',
                lineHeight: '1.5'
              }}>
                {notification.title}
              </span>
              {notification.groupedCount > 1 && (
                <Tag size="small" color="geekblue" style={{ fontSize: '11px', margin: 0 }}>
                  {notification.groupedCount} updates
                </Tag>
              )}
              {!notification.read && (
                <Badge dot style={{ backgroundColor: '#1890ff' }} />
              )}
              {notification.read && (
                <CheckOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
              )}
            </div>
          }
          description={
            <div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                flexWrap: 'wrap',
                marginBottom: '6px'
              }}>
                <Tag size="small" color="blue" style={{ fontSize: '11px', margin: 0 }}>
                  {typeName}
                </Tag>
                <Tag 
                  size="small" 
                  color={getStatusTagColor(statusName)}
                  style={{ fontSize: '11px', margin: 0 }}
                >
                  {statusName}
                </Tag>
                {notification.originalData?.language && (
                  <Tag size="small" style={{ fontSize: '11px', margin: 0 }}>
                    {notification.originalData.language.toUpperCase()}
                  </Tag>
                )}
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: '#8c8c8c',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <ClockCircleOutlined style={{ fontSize: '10px' }} />
                {moment(notification.timestamp).fromNow()}
              </div>
            </div>
          }
        />
      </List.Item>
    );
  };

  const tabItems = [
    {
      key: 'all',
      label: <span style={{ fontSize: '13px' }}>All</span>,
      children: (
        <List
          className="notification-list custom-scrollbar notification-list-all"
          itemLayout="horizontal"
          dataSource={notifications}
          renderItem={renderNotificationItem}
          locale={{
            emptyText: (
              <Empty 
                description={
                  <span style={{ fontSize: '13px', color: '#8c8c8c' }}>
                    No notifications
                  </span>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: '40px 0' }}
              />
            )
          }}
          style={{ 
            maxHeight: 'calc(100vh - 280px)', 
            overflowY: 'auto',
            margin: 0,
            padding: 0
          }}
        />
      )
    },
    {
      key: 'unread',
      label: (
        <Badge count={unreadCount} size="small" offset={[4, 0]}>
          <span style={{ fontSize: '13px', paddingRight: '4px' }}>Unread</span>
        </Badge>
      ),
      children: (
        <List
          className="notification-list custom-scrollbar notification-list-unread"
          itemLayout="horizontal"
          dataSource={notifications.filter(n => !n.read)}
          renderItem={renderNotificationItem}
          locale={{
            emptyText: (
              <Empty 
                description={
                  <span style={{ fontSize: '13px', color: '#8c8c8c' }}>
                    No unread notifications
                  </span>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: '40px 0' }}
              />
            )
          }}
          style={{ 
            maxHeight: 'calc(100vh - 280px)', 
            overflowY: 'auto',
            margin: 0,
            padding: 0
          }}
        />
      )
    }
  ];

  return (
    <div className="notification-panel" style={{ 
      width: '100vw',
      maxWidth: '400px',
      backgroundColor: '#ffffff'
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h3 style={{ 
          margin: 0, 
          fontSize: '16px', 
          fontWeight: 600,
          color: '#262626'
        }}>
          Notifications
        </h3>
        <Space size={4}>
          <Tooltip title={unreadCount > 0 ? `Mark ${unreadCount} unread notification${unreadCount === 1 ? '' : 's'} as read` : 'All notifications are read'}>
            <Button
              type="text"
              icon={<CheckCircleOutlined />}
              onClick={markAllAsRead}
              disabled={!markAllAsRead || unreadCount === 0}
              size="small"
              aria-label="Mark all notifications as read"
              style={{
                width: '28px',
                height: '28px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </Tooltip>
          <Tooltip title="Filter notifications">
            <Button
              type={hasActiveFilters ? "primary" : "text"}
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
              size="small"
              style={{ 
                width: '28px',
                height: '28px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </Tooltip>
          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={refreshNotifications}
            loading={isLoadingNotifications}
            size="small"
            style={{ 
              width: '28px',
              height: '28px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onClose}
            size="small"
            style={{ 
              width: '28px',
              height: '28px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        </Space>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div style={{
        padding: '12px 16px',
          backgroundColor: '#e6f7ff',
          borderBottom: '1px solid #91d5ff'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <span style={{ 
              fontSize: '13px', 
              fontWeight: 500,
              color: '#1890ff'
            }}>
              Filter Notifications
            </span>
            {hasActiveFilters && (
              <Button 
                type="link" 
                size="small" 
                icon={<ClearOutlined />}
                onClick={clearFilters}
                style={{ 
                  fontSize: '12px',
                  padding: 0,
                  height: 'auto'
                }}
              >
                Clear All
              </Button>
            )}
          </div>
          
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <div className="notification-filter-row" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Select
                placeholder="Type"
                allowClear
                className="notification-filter-select"
                style={{ flex: 1, minWidth: '120px' }}
                value={notificationFilters.type}
                onChange={handleTypeFilter}
                options={typeOptions}
                size="small"
                dropdownStyle={{ fontSize: '12px' }}
              />
              <Select
                placeholder="Status"
                allowClear
                className="notification-filter-select"
                style={{ flex: 1, minWidth: '120px' }}
                value={notificationFilters.status}
                onChange={handleStatusFilter}
                options={statusOptions}
                size="small"
                dropdownStyle={{ fontSize: '12px' }}
              />
            </div>
            <div className="notification-filter-row" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Select
                placeholder="Language"
                allowClear
                className="notification-filter-select"
                style={{ flex: 1, minWidth: '120px' }}
                value={notificationFilters.language}
                onChange={handleLanguageFilter}
                options={languageOptions}
                size="small"
                dropdownStyle={{ fontSize: '12px' }}
              />
              <RangePicker
                placeholder={['From', 'To']}
                className="notification-filter-datepicker"
                style={{ flex: 1, minWidth: '120px' }}
                size="small"
                value={[
                  notificationFilters.dateFrom ? moment(notificationFilters.dateFrom) : null,
                  notificationFilters.dateTo ? moment(notificationFilters.dateTo) : null
                ]}
                onChange={handleDateRangeChange}
              />
            </div>
          </Space>
        </div>
      )}

      {/* Tabs */}
      <Spin spinning={isLoadingNotifications} size="small">
        <Tabs 
          items={tabItems} 
          style={{ 
            margin: 0,
            padding: '0 16px'
          }}
          tabBarStyle={{ 
            marginBottom: 0,
            borderBottom: '1px solid #f0f0f0'
          }}
        />
      </Spin>

      {/* Load More */}
      {notificationMetadata?.hasNext && (
        <div style={{
          padding: '12px 20px',
          textAlign: 'center',
          borderTop: '1px solid #f0f0f0'
        }}>
          <Button
            type="link"
            onClick={loadMoreNotifications}
            loading={isLoadingNotifications}
            size="small"
            style={{ fontSize: '13px' }}
          >
            Load More
          </Button>
        </div>
      )}

      {/* View All Link */}
      <div style={{
        padding: '10px 16px',
        textAlign: 'center',
        borderTop: '1px solid #f0f0f0',
        backgroundColor: '#fafafa'
      }}>
        <Button
          type="link"
          onClick={() => {
            navigate('/notifications');
            onClose?.();
          }}
          style={{ fontSize: '13px', fontWeight: 500 }}
        >
          View All Notifications →
        </Button>
      </div>
    </div>
  );
};

export default NotificationPanel;
